import BlobIcon from '@totallymoney/ui/components/BlobIcon'
import Box from '@totallymoney/ui/components/Box'
import Divider from '@totallymoney/ui/components/Divider'
import Heading from '@totallymoney/ui/components/Heading'
import Modal from '@totallymoney/ui/components/Modal'
import Stack from '@totallymoney/ui/components/Stack'
import Text from '@totallymoney/ui/components/Text'

import { LoanName } from '@appTypes/LoansApi.interface'

import { getDisplayLoanName } from '@utils/getDisplayLoanName'

interface LoanTypeModalProps {
    isOceanSecureLoan?: boolean
    loanName?: string
    onModalClose: () => void
    showModal: boolean
}

const loanNameDescriptions: Record<string, string> = {
    [LoanName.PersonalLoan]:
        'Personal loans, sometimes called unsecured loans, let you borrow an agreed amount of money, which is paid directly into your bank account. You’ll repay it over a set amount of time in fixed monthly instalments, usually with a fixed interest rate. Interest rates vary depending on your credit history, and some personal loans have a fee if you pay them back early.',
    [LoanName.GuarantorLoan]:
        'This type of loan is where someone — a guarantor — agrees to cover your repayments if you can’t. Guarantor loans are good if you’re struggling to get a personal loan, as having a guarantor gives lenders confidence they’re likely to get their money back. A guarantor should be someone you trust who has a good credit history. Like other loans, there’s usually a fixed interest rate and you’ll pay it back over a set period.',
    [LoanName.SecuredLoan]:
        'A homeowner loan is for those who own a home, either outright or with a mortgage.\n\n' +
        'The loan is secured against your home, so you can often borrow a larger amount of money than an personal loan. Loans are typically between £10,000 and £100,000.',
    [LoanName.CarFinanceBroker]:
        'This loan is arranged through a car finance broker, which is someone who helps you find and arrange your car finance loan. Brokers work with a variety of lenders to find the best finance option for you, as well as helping you find your car. Choosing a car finance broker on TotallyMoney means we’ll connect you to the broker.',
    [LoanName.HirePurchase]:
        'This is a type of car finance loan. Hire Purchase (HP) agreements let you put down a deposit on a car, then pay off the rest in monthly instalments, plus interest, until 100% of the car is owned at the end of the term. You can apply for an HP agreement before going to the dealership, so you know how much you can borrow in advance. But, if you can’t keep up with the repayments, the finance company can take your car. ',
}

const LoanTypeModal = ({
    loanName,
    showModal,
    onModalClose,
    isOceanSecureLoan,
}: LoanTypeModalProps) => {
    return (
        <Modal
            title={isOceanSecureLoan ? 'What’s a homeowner loan?' : loanName}
            hasHeader={false}
            hasTooltipHeader={true}
            isOpen={showModal}
            onClose={onModalClose}
            isFullScreen={true}
            hasMinHeight={false}
        >
            {loanName != null ? (
                <>
                    <Text variant="100" color="light">
                        {loanNameDescriptions[loanName]}
                    </Text>

                    {isOceanSecureLoan ? (
                        <Box>
                            <Divider variant="secondary" />

                            <Stack direction="row" space="space20" mb="space20">
                                <BlobIcon blobName="info" />
                                <Box display="flex" alignItems="center">
                                    <Heading variant="40" as="h1">
                                        What’s APRC?
                                    </Heading>
                                </Box>
                            </Stack>

                            <Stack direction="column" space="space30">
                                <Text variant="100" color="light">
                                    APRC stands for Annual Percentage Rate of
                                    Charge. This shows the cost of a homeowner
                                    loan over its full length or lifetime,
                                    representing how different rates and charges
                                    could have an effect.
                                </Text>

                                <Text variant="100" color="light">
                                    APRC could help you work out the overall
                                    costs of different loans, from various
                                    lenders. For example, some homeowner loans
                                    could have an introductory or promotional
                                    rate, but after a certain period of time,
                                    may switch to a higher variable rate.
                                </Text>
                            </Stack>
                        </Box>
                    ) : null}
                </>
            ) : null}
        </Modal>
    )
}

export default LoanTypeModal
